import styled from 'styled-components'
import { rem, transitions } from 'polished'
import { GatsbyImage } from 'gatsby-plugin-image'

import { Title16, Title20, Title26, Title32 } from 'components/Typography'
import MarkdownRenderer from 'components/MarkdownRenderer'

export const Package = styled.article`
  width: calc(100vw - 36px);
  margin: 0 auto;
  display: flex;
  flex-flow: column nowrap;
  row-gap: 20px;
  padding-top: ${rem(74)};

  ${({ theme }) => theme.mediaQueries.medium} {
    width: calc(50vw - 24px);
    margin-left: max(8px, calc((100vw - 1342px) / 2));
    display: inline-flex;

    &:last-of-type {
      display: flex;
      margin: 0 auto;
    }

    &:nth-child(even) {
      margin-left: 16px;

      &:last-of-type {
        margin-top: initial;
        margin-right: initial;
        margin-bottom: initial;
        display: inline-flex;
      }
    }
  }

  ${({ theme }) => theme.mediaQueries.large} {
    width: min(calc(50vw - 170px), 656px);
    margin-left: max(170px, calc((100vw - 1342px) / 2));

    &:last-of-type {
      padding-top: ${rem(152)};
    }

    &:nth-child(even) {
      margin-left: 30px;

      &:last-of-type {
        margin-top: initial;
        margin-right: initial;
        margin-bottom: initial;
        display: inline-flex;
      }
    }
  }
`

export const PackageTitle = styled(Title32)`
  font-size: ${rem(19.2)};
  text-align: center;
  width: 100%;

  ${({ theme }) => theme.mediaQueries.medium} {
    font-size: ${rem(24)};
  }

  ${({ theme }) => theme.mediaQueries.large} {
    font-size: ${rem(32)};
  }
`

export const HighlightTagWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-content: center;
  align-items: center;
  column-gap: 3.79px;
  row-gap: 3.79px;

  ${({ theme }) => theme.mediaQueries.medium} {
    column-gap: 7.5px;
    row-gap: 7.5px;
    justify-content: center;
  }
`

export const HighlightTag = styled.span`
  font-size: ${rem(8)};
  font-weight: 400;
  font-family: BrownStd-Regular;
  display: inline-block;
  background-color: ${({ theme }) => theme.colors.whiteChocolateToMushroomGrey};
  padding: 5.5px 16px;
  line-height: 160%;
  text-align: center;
  letter-spacing: 0.134px;
  align-self: center;
  margin: 0 auto;
  flex: 1;
  min-width: 115px;

  ${({ theme }) => theme.mediaQueries.medium} {
    min-width: 180px;
    margin: initial;
    flex: initial;
    font-size: ${rem(12)};
  }
`

export const PackageCard = styled.div`
  max-height: 785px;
  width: 100%;
  flex: 1;
  display: flex;
  flex-flow: column nowrap;
  box-shadow: 0px 6.72px 13.44px 0px
    ${({ theme }) => theme.colors.whiteChocolateToMushroomGrey};
`

export const CardMedia = styled(GatsbyImage)`
  width: 100%;
  max-height: 268.56px;
`

export const CardBody = styled.div`
  padding: 0px 25px 25px 25px;
  display: flex;
  flex-flow: column nowrap;

  ${({ theme }) => theme.mediaQueries.medium} {
    padding: 0px 30.72px 45.32px 30.72px;
  }
`

export const CardTitle = styled(Title26)`
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divisor};
  padding-bottom: 19.2px;
  margin-top: 24px;
  margin-bottom: 19.2px;
  text-align: center;
  font-size: ${rem(22)};

  ${({ theme }) => theme.mediaQueries.large} {
    font-size: ${rem(26)};
    text-align: left;
    letter-spacing: 0.13px;
  }
`

export const PackItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-bottom: ${rem(16)};

  &:last-child {
    padding-bottom: initial;
  }

  ${({ theme }) => theme.mediaQueries.medium} {
    justify-content: flex-start;
  }
`

export const PackItemName = styled(Title16)`
  color: ${({ theme }) => theme.colors.beaver};
  font-family: BrownStd-Regular;
  font-weight: 400;
  text-align: center;
  font-size: ${rem(16)};

  ${({ theme }) => theme.mediaQueries.medium} {
    text-align: initial;
  }

  ${({ theme }) => theme.mediaQueries.large} {
    font-size: ${rem(19.2)};
  }
`

export const PackItemDescription = styled(MarkdownRenderer)`
  & *,
  & p,
  & span {
    font-family: BrownStd-light;
    font-weight: 300;
    font-size: ${rem(14)};
    color: ${({ theme }) => theme.colors.darkTeal};
    text-align: center;
    margin: initial;

    ${({ theme }) => theme.mediaQueries.medium} {
      text-align: initial;
    }
  }
`

export const PackBookCta = styled.button`
  ${transitions('background 0.3s', 'color 0.3s')}
  width: 100%;
  max-width: 180px;
  padding: 6px 12px;
  background-color: ${({ theme }) => theme.colors.darkTealToNaturalGreen};
  border: 1px solid ${({ theme }) => theme.colors.darkTealToNaturalGreen};
  color: ${({ theme }) => theme.colors.whiteToSandstone};
  line-height: 100%;
  font-size: ${rem(14)};
  margin-top: 24px;
  margin-right: auto;
  margin-left: auto;

  &:hover,
  &:focus,
  &:active {
    background: ${({ theme }) => theme.colors.whiteToSandstone};
    border-color: 1px solid
      ${({ theme }) => theme.colors.darkTealToNaturalGreen};
    color: ${({ theme }) => theme.colors.darkTealToNaturalGreen};
  }

  ${({ theme }) => theme.mediaQueries.large} {
    font-size: ${rem(16)};
    margin-top: 32px;
    font-family: BrownStd-Regular;
    font-weight: 400;
    line-height: 25.6px;
    letter-spacing: 0.134px;
  }
`

export const PackMainPrice = styled(Title20)`
  padding-top: 24px;
  text-align: center;
  font-size: ${rem(16)};
  color: ${({ theme }) => theme.colors.darkTeal};
  font-family: SangbleuKindgdom-Light;

  ${({ theme }) => theme.mediaQueries.medium} {
    font-family: SangbleuKindgdom-Light-Italic;
    text-align: initial;
    font-size: ${rem(20)};
    line-height: 140%;
    letter-spacing: 0.13px;
    }
    
    ${({ theme }) => theme.mediaQueries.large} {
      font-size: ${rem(20)};
    }
  }
`

export const PackPricesWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  padding-top: 24px;
`

export const PackPrice = styled.span`
  font-family: BrownStd-Light;
  font-weight: 300;
  font-size: ${rem(14)};
  color: ${({ theme }) => theme.colors.darkTeal};

  ${({ theme }) => theme.mediaQueries.medium} {
    font-size: ${rem(16.8)};
    line-height: 100%;
  }
`

export const VerticalSeparator = styled.div`
  width: 1px;
  background: ${({ theme }) => theme.colors.darkTeal};
  height: 75%;
  display: inline-block;
  margin: 0px 16px;
  align-sef: stretch;
`

export const TermsAndConditions = styled.button`
  border: none;
  background: transparent;
  padding-top: ${rem(14)};
  font-family: BrownStd-light;
  font-weight: 300;
  font-size: ${rem(14)};
  color: ${({ theme }) => theme.colors.darkTeal};
  text-align: center;
  margin: initial;
  display: block;
  width: auto;
  text-decoration: underline;

  ${({ theme }) => theme.mediaQueries.medium} {
    text-align: initial;
    padding-top: ${rem(24)};
  }
`
